import React, { FunctionComponent, ReactNode, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

const Container = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: 'transparent',
  flexGrow: 1,
  flexDirection: 'column',
  minHeight: '150px',
  gap: theme.spacing['24px'],
  ...(theme.breakpoints.isMobileSized && {
    padding: `${theme.spacing['24px']} ${theme.spacing['16px']} ${theme.spacing['32px']}`,
    gap: 0,
  }),
}))

const Header = styled.View<{
  hasTitle: boolean
  theme: ThemeType
}>(({ hasTitle, theme }) => ({
  flexDirection: 'row',
  justifyContent: hasTitle ? 'space-between' : 'flex-end',
  backgroundColor: 'transparent',
  alignItems: 'flex-start',
  ...(IS_WEB && { cursor: 'default' }),
  ...(theme.breakpoints.isMobileSized && {
    marginTop: theme.spacing['20px'],
  }),
}))

const ContentsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  zIndex: 1,
  ...(theme.breakpoints.isMobileSized && {
    marginTop: theme.spacing['8px'],
  }),
}))

const ButtonsContainer = styled.View<{ theme: ThemeType; singleLineButtons?: boolean }>(
  ({ theme, singleLineButtons }) => ({
    flexDirection: theme.breakpoints.isMinWidthLaptop || singleLineButtons ? 'row-reverse' : 'column',
    justifyContent: 'flex-start',
    gap: theme.spacing['16px'],
    ...(theme.breakpoints.isMobileSized && {
      marginTop: theme.spacing['32px'],
      gap: theme.spacing['8px'],
    }),
  }),
)

const ConfirmButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['24px'],
  alignSelf: 'center',
  ...(theme.breakpoints.isMobileSized && {
    width: '100%',
  }),
}))

const CancelButton = styled(TertiaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['24px'],
  alignSelf: 'center',
  ...(theme.breakpoints.isMobileSized && {
    width: '100%',
  }),
}))

export interface ConfirmationModalProps {
  allowEmptyContents?: boolean
  cancelButtonText?: ReactNode
  cancelButtonTextColor?: string
  confirmationButtonText: ReactNode
  confirmButtonDisabled?: boolean
  confirmButtonLoading?: boolean
  handleAnimationEnd?: () => void
  modalContents: ReactNode
  modalTitle?: ReactNode
  onConfirmationButtonPress: () => void
  onRequestClose: () => void
  visible: boolean
  width?: string
  onCancelPressed?: () => void
  showCancel?: boolean
  testID?: string
  singleLineButtons?: boolean
  closeOnScrim?: boolean
  disableClose?: boolean
  showCloseIcon?: boolean
  cancelButtonLoading?: boolean
  cancelButtonDisabled?: boolean
  containerStyle?: ViewStyle
  contentsContainerStyle?: ViewStyle
  /** Overrides the default title and close control. */
  customHeader?: ReactNode
  modalStyle?: ViewStyle
}

export const ConfirmationModal: FunctionComponent<ConfirmationModalProps> = ({
  allowEmptyContents = false,
  cancelButtonText,
  cancelButtonTextColor = '#017A8D',
  confirmationButtonText,
  confirmButtonDisabled = false,
  confirmButtonLoading = false,
  handleAnimationEnd = () => {},
  modalContents,
  modalTitle,
  onRequestClose,
  onConfirmationButtonPress,
  visible,
  width = '544px',
  onCancelPressed,
  showCancel = true,
  testID = 'ConfirmationModal',
  singleLineButtons = false,
  closeOnScrim = false,
  disableClose = false,
  showCloseIcon = true,
  cancelButtonLoading = false,
  cancelButtonDisabled = false,
  containerStyle,
  contentsContainerStyle,
  customHeader,
  modalStyle,
}) => {
  const { formatMessage } = useIntl()
  const { breakpoints, colors } = useTheme()
  const ref = useRef<BottomSheetGorhom>(null)

  const closeBottomSheet = useCallback(() => {
    ref?.current?.close()
  }, [])

  const modalContentsWithButtons = (
    <Container testID={tID(`${testID}-container`)} style={containerStyle}>
      {customHeader ?? (
        <Header hasTitle={Boolean(modalTitle)} testID={tID(`${testID}-header`)}>
          {modalTitle && <Title size={TitleSize.MEDIUM} text={modalTitle} />}
          {showCloseIcon && (
            <PressableOpacity
              onPress={onRequestClose}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close modal',
                description: 'Accessibility label for confirmation modal dialog close button',
              })}
              testID={tID(`${testID}-close-icon`)}
            >
              <XIcon size={24} fillColor={colors.iconDefault} />
            </PressableOpacity>
          )}
        </Header>
      )}
      {modalContents && (
        <ContentsContainer style={contentsContainerStyle}>
          <BodyText text={modalContents} />
        </ContentsContainer>
      )}
      <ButtonsContainer singleLineButtons={singleLineButtons}>
        <ConfirmButton
          onPress={onConfirmationButtonPress}
          testID={tID(`${testID}-continueButton`)}
          text={confirmationButtonText}
          disabled={confirmButtonDisabled}
          loading={confirmButtonLoading}
        />
        {showCancel && (
          <CancelButton
            onPress={onCancelPressed ? onCancelPressed : onRequestClose}
            testID={tID(`${testID}-close-button`)}
            text={cancelButtonText}
            customTextColor={cancelButtonTextColor}
            disabled={cancelButtonDisabled}
            loading={cancelButtonLoading}
          />
        )}
      </ButtonsContainer>
    </Container>
  )

  if (!modalContents && !allowEmptyContents) {
    return null
  }

  return (
    <Modal
      modalTitle={modalTitle}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onCloseEnd={() => {
        if (breakpoints?.isMobileSized) {
          closeBottomSheet()
        }
        onRequestClose()
      }}
      visible={visible}
      bottomSheetRef={ref}
      modalContents={modalContentsWithButtons}
      closeOnScrim={closeOnScrim}
      disableClose={disableClose}
      useDynamicHeightForBottomSheet
      width={width}
      style={modalStyle}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Confirmation modal',
        description: 'Accessibility label for confirmation modal dialog',
      })}
    />
  )
}
